import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/cn'

/**
 * Radix throws if a `Tooltip.Root` renders without a provider above it. Rather
 * than making every leaf component (a copy button inside a toast, say) depend
 * on app-level composition, we track whether *our* provider is mounted and
 * fall back to a local one. Grouped hover-delay skipping still works wherever
 * the app-level provider is present.
 *
 * A tooltip is always supplementary here. Nothing in this app is reachable
 * only by hovering — every tooltip target also carries a visible label or an
 * `aria-label`, because on a phone there is no hover at all.
 */
const HasProvider = React.createContext(false)

const DEFAULT_DELAY = 260

export function TooltipProvider(p: { children: React.ReactNode }) {
  return (
    <HasProvider.Provider value={true}>
      <TooltipPrimitive.Provider delayDuration={DEFAULT_DELAY} skipDelayDuration={300}>
        {p.children}
      </TooltipPrimitive.Provider>
    </HasProvider.Provider>
  )
}

export function Tooltip(p: {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  delay?: number
}) {
  const hasProvider = React.useContext(HasProvider)

  // Nothing to describe — render the trigger untouched rather than an empty box.
  if (p.content === null || p.content === undefined || p.content === '') {
    return <>{p.children}</>
  }

  const root = (
    <TooltipPrimitive.Root delayDuration={p.delay ?? DEFAULT_DELAY}>
      <TooltipPrimitive.Trigger asChild>{p.children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={p.side ?? 'top'}
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            // Capped against the viewport, not just at 280px: a 320px tooltip on
            // a 360px phone would sit half off-screen.
            'z-[70] max-w-[min(280px,calc(100vw-2rem))] rounded-md border border-line',
            'bg-s2 px-2.5 py-1.5 text-[12.5px] leading-[1.5] text-fg shadow-e2',
            'origin-(--radix-tooltip-content-transform-origin)',
            'transition-[opacity,transform] duration-100 ease-out',
            'starting:scale-[0.97] starting:opacity-0'
          )}
        >
          {p.content}
          <TooltipPrimitive.Arrow className="fill-s2" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )

  if (hasProvider) return root

  return (
    <TooltipPrimitive.Provider delayDuration={p.delay ?? DEFAULT_DELAY} skipDelayDuration={300}>
      {root}
    </TooltipPrimitive.Provider>
  )
}
